import React from 'react';

/**
 * Language flags, drawn inline (no image assets, no emoji font quirks — the
 * mobile app draws the same three with react-native-svg).
 *
 * English flies the flag of the British East India Company: US-style red/white
 * stripes married to the Union Jack. Spanish flies Costa Rica, where Yarnboard
 * is made. Portuguese flies Brazil, where its speakers are.
 */

const EN_STRIPES = 13; // red↔white — as on the EIC (and, descended from it, the US) flag

/** A compact Union Jack, drawn in its own nested SVG so it clips to the canton. */
function UnionJack({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const wDiag = h * 0.30, rDiag = h * 0.12;
  const wCross = h * 0.34, rCross = h * 0.20;
  return (
    <svg x={x} y={y} width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <rect width={w} height={h} fill="#012169" />
      {/* white saltire (St Andrew) then red saltire (St Patrick) */}
      <path d={`M0,0 L${w},${h} M${w},0 L0,${h}`} stroke="#fff" strokeWidth={wDiag} />
      <path d={`M0,0 L${w},${h} M${w},0 L0,${h}`} stroke="#c8102e" strokeWidth={rDiag} />
      {/* white then red St George's cross */}
      <rect x={(w - wCross) / 2} y={0} width={wCross} height={h} fill="#fff" />
      <rect x={0} y={(h - wCross) / 2} width={w} height={wCross} fill="#fff" />
      <rect x={(w - rCross) / 2} y={0} width={rCross} height={h} fill="#c8102e" />
      <rect x={0} y={(h - rCross) / 2} width={w} height={rCross} fill="#c8102e" />
    </svg>
  );
}

/** British East India Company flag: red/white stripes + a Union Jack canton. */
function EastIndiaCompanyFlag({ h }: { h: number }) {
  const w = h * 1.5;
  const sh = h / EN_STRIPES;
  const cantonW = w * 0.42;
  const cantonH = sh * 7;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="English">
      {Array.from({ length: EN_STRIPES }).map((_, i) => (
        <rect key={i} x={0} y={i * sh} width={w} height={sh + 0.3} fill={i % 2 === 0 ? '#b22234' : '#fff'} />
      ))}
      <UnionJack x={0} y={0} w={cantonW} h={cantonH} />
    </svg>
  );
}

/** Flag of Costa Rica: blue, white, red (double-height), white, blue. */
function CostaRicaFlag({ h }: { h: number }) {
  const w = h * 1.5;
  return (
    <svg width={w} height={h} viewBox="0 0 30 20" role="img" aria-label="Español">
      <rect x={0} y={0} width={30} height={20} fill="#002b7f" />
      <rect x={0} y={4} width={30} height={12} fill="#fff" />
      <rect x={0} y={7} width={30} height={6} fill="#ce1126" />
    </svg>
  );
}

/** Flag of Brazil: green field, yellow lozenge, blue globe. */
function BrazilFlag({ h }: { h: number }) {
  const w = h * 1.5;
  // The switcher and the settings panel can both be on screen; a shared clip id
  // would make the second flag reuse the first one's definition.
  const clipId = `yb-br-globe-${React.useId()}`;
  return (
    <svg width={w} height={h} viewBox="0 0 30 20" role="img" aria-label="Português">
      <rect x={0} y={0} width={30} height={20} fill="#009c3b" />
      <path d="M15,2.4 L27.2,10 L15,17.6 L2.8,10 Z" fill="#ffdf00" />
      <circle cx={15} cy={10} r={4.2} fill="#002776" />
      {/* the Ordem e Progresso band, clipped to the globe */}
      <clipPath id={clipId}><circle cx={15} cy={10} r={4.2} /></clipPath>
      <path d="M10.4,11.6 Q15,8.6 19.7,11.9" stroke="#fff" strokeWidth={1.1} fill="none" clipPath={`url(#${clipId})`} />
    </svg>
  );
}

export type FlagLang = 'en' | 'es' | 'pt';

export default function LangFlag({ lang, size = 18 }: { lang: FlagLang; size?: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        lineHeight: 0,
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
      }}
    >
      {lang === 'en' && <EastIndiaCompanyFlag h={size} />}
      {lang === 'es' && <CostaRicaFlag h={size} />}
      {lang === 'pt' && <BrazilFlag h={size} />}
    </span>
  );
}
